import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  User, Phone, Mail, Calendar, Clock, Tag,
  DollarSign, FileText, Activity, ArrowRight,
  Globe, MessageSquare, MapPin
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stageLabels = {
  cold_data: 'Cold Data',
  attempting_contact: 'Attempting Contact',
  contacted: 'Contacted',
  qualified: 'Qualified',
  consultation_scheduled: 'Consultation Scheduled',
  consultation_completed: 'Consultation Completed',
  warm_follow_up: 'Warm Follow-Up',
  rsvp_office_event: 'RSVP Office Event',
  attended_office_event: 'Attended Office Event',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
  long_term_nurture: 'Long-Term Nurture'
};

const stageColors = {
  cold_data: 'bg-slate-100 text-slate-700',
  attempting_contact: 'bg-blue-100 text-blue-800',
  contacted: 'bg-indigo-100 text-indigo-800',
  qualified: 'bg-purple-100 text-purple-800',
  consultation_scheduled: 'bg-cyan-100 text-cyan-800',
  consultation_completed: 'bg-teal-100 text-teal-800',
  warm_follow_up: 'bg-amber-100 text-amber-800',
  rsvp_office_event: 'bg-orange-100 text-orange-800',
  attended_office_event: 'bg-lime-100 text-lime-800',
  closed_won: 'bg-green-100 text-green-800',
  closed_lost: 'bg-red-100 text-red-700',
  long_term_nurture: 'bg-violet-100 text-violet-800'
};

const activityIcons = {
  status_change: ArrowRight,
  note_added: FileText,
  assignment_change: User,
  tag_change: Tag,
  communication: MessageSquare,
  deposit_received: DollarSign,
  no_show_flagged: Clock,
  field_update: FileText,
  created: Activity,
};

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5">
      <Icon size={14} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-slate-700 break-words">{value}</p>
      </div>
    </div>
  );
}

export default function LeadDetailSheet({ open, onOpenChange, leadId }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    if (open && leadId) {
      setLoading(true);
      setActivityLoading(true);

      const doFetch = async () => {
        try {
          const res = await fetch(`${API_URL}/api/leads/${leadId}`, { credentials: 'include' });
          if (res.ok) setLead(await res.json());
        } catch (e) {
          logger.error('Error fetching lead:', e);
        } finally {
          setLoading(false);
        }

        try {
          const res = await fetch(`${API_URL}/api/leads/${leadId}/activities`, { credentials: 'include' });
          if (res.ok) setActivities(await res.json());
        } catch (e) {
          logger.error('Error fetching activities:', e);
        } finally {
          setActivityLoading(false);
        }
      };

      doFetch();
    }
  }, [open, leadId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      return format(d, 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
      return format(d, 'MMM d, h:mm a');
    } catch {
      return dateStr;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] sm:w-[480px] p-0 flex flex-col" data-testid="lead-detail-sheet">
        <SheetHeader className="px-5 pt-5 pb-3">
          <SheetTitle className="text-base">Lead Details</SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-5 pb-5">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
            ) : lead ? (
              <>
                {/* Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-900" data-testid="lead-detail-name">
                    {lead.first_name} {lead.last_name || ''}
                  </h3>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge className={stageColors[lead.stage] || 'bg-slate-100'}>
                      {stageLabels[lead.stage] || lead.stage}
                    </Badge>
                    {lead.lead_source && (
                      <Badge variant="outline" className="text-[10px]">
                        {lead.lead_source}
                      </Badge>
                    )}
                    {lead.finance_setup_pending && (
                      <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">Finance Pending</Badge>
                    )}
                  </div>
                </div>

                <Separator className="my-3" />

                {/* Contact Info */}
                <div className="space-y-0.5">
                  <InfoRow icon={Phone} label="Phone" value={lead.phone} />
                  <InfoRow icon={Mail} label="Email" value={lead.email} />
                  <InfoRow icon={Globe} label="Source" value={lead.lead_source} />
                  <InfoRow icon={MapPin} label="Product Interest" value={lead.product_interest} />
                  {lead.estimated_value && (
                    <InfoRow icon={DollarSign} label="Estimated Value" value={`$${lead.estimated_value.toLocaleString()}`} />
                  )}
                  <InfoRow icon={Clock} label="Investment Timeline" value={lead.investment_timeline} />
                  <InfoRow icon={MessageSquare} label="Language" value={lead.language_preference} />
                  <InfoRow icon={Calendar} label="Created" value={formatDate(lead.created_at)} />
                </div>

                {/* Notes */}
                {lead.notes && (
                  <>
                    <Separator className="my-3" />
                    <div>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Notes</p>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2.5">{lead.notes}</p>
                    </div>
                  </>
                )}

                {/* Deposit Info */}
                {lead.deposit_received && (
                  <>
                    <Separator className="my-3" />
                    <div className="bg-green-50 border border-green-200 rounded p-2.5">
                      <p className="text-xs font-medium text-green-800">Deposit Received</p>
                      {lead.deposit_amount && <p className="text-sm text-green-700">${lead.deposit_amount.toLocaleString()}</p>}
                      {lead.deposit_date && <p className="text-xs text-green-600">{lead.deposit_date}</p>}
                    </div>
                  </>
                )}

                {/* No-Show Info */}
                {lead.no_show_count > 0 && (
                  <>
                    <Separator className="my-3" />
                    <div className="bg-red-50 border border-red-200 rounded p-2.5">
                      <p className="text-xs font-medium text-red-800">No-Shows: {lead.no_show_count}</p>
                      {lead.last_no_show_at && <p className="text-xs text-red-600">Last: {formatDate(lead.last_no_show_at)}</p>}
                    </div>
                  </>
                )}

                <Separator className="my-4" />

                {/* Activity History */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5">
                    <Activity size={14} />
                    Activity History
                  </h4>

                  {activityLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
                    </div>
                  ) : activities.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No activity recorded yet</p>
                  ) : (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />

                      <div className="space-y-3">
                        {activities.map((act, idx) => {
                          const IconComp = activityIcons[act.activity_type] || Activity;
                          return (
                            <div key={act.activity_id || idx} className="flex gap-3 relative" data-testid={`activity-item-${idx}`}>
                              <div className="shrink-0 w-[15px] h-[15px] rounded-full bg-white border-2 border-slate-300 flex items-center justify-center z-10 mt-0.5">
                                <IconComp size={8} className="text-slate-500" />
                              </div>
                              <div className="min-w-0 flex-1 pb-1">
                                <p className="text-xs text-slate-700 leading-relaxed">{act.description}</p>
                                {act.notes && (
                                  <p className="text-[11px] text-slate-500 mt-0.5 italic">"{act.notes}"</p>
                                )}
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-slate-400">
                                    {formatShortDate(act.created_at)}
                                  </span>
                                  {act.created_by_name && (
                                    <span className="text-[10px] text-slate-400">
                                      by {act.created_by_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">Lead not found</p>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
